import { useMutation, useQuery } from "@tanstack/react-query";
import { useAdminApplication, useAdminSession } from "../../app/providers";
import { memberKeys } from "../members/queries";
import type { Schemas } from "../workspace/api.generated";
import {
  assignInquiry,
  findInquiryForReconciliation,
  getInquiry,
  getInquiries,
  replyInquiry,
} from "./api";
import type { InquiryListQuery } from "./api";

export const inquiryKeys = {
  all: (sessionEpoch: number) => ["admin", sessionEpoch, "inquiries"] as const,
  lists: (sessionEpoch: number) => [...inquiryKeys.all(sessionEpoch), "list"] as const,
  list: (sessionEpoch: number, query: InquiryListQuery) => (
    [...inquiryKeys.lists(sessionEpoch), query] as const
  ),
  details: (sessionEpoch: number) => [...inquiryKeys.all(sessionEpoch), "detail"] as const,
  detail: (sessionEpoch: number, inquiryId: number) => (
    [...inquiryKeys.details(sessionEpoch), inquiryId] as const
  ),
};

export function useInquiryList(query: InquiryListQuery) {
  const { apiClient } = useAdminApplication();
  const session = useAdminSession();
  return useQuery({
    queryKey: inquiryKeys.list(session.sessionEpoch, query),
    queryFn: ({ signal }) => getInquiries(apiClient, query, signal),
    refetchOnMount: "always",
    staleTime: 0,
  });
}

export function useInquiryDetail(inquiryId?: number) {
  const { apiClient } = useAdminApplication();
  const session = useAdminSession();
  return useQuery({
    queryKey: inquiryKeys.detail(session.sessionEpoch, inquiryId ?? 0),
    queryFn: ({ signal }) => getInquiry(apiClient, inquiryId!, signal),
    enabled: inquiryId !== undefined,
    staleTime: 0,
  });
}

type InquiryMutation =
  | { readonly kind: "assign"; readonly inquiryId: number }
  | { readonly kind: "reply"; readonly inquiryId: number; readonly body: Schemas["CreateInquiryReplyRequest"] };

export function useInquiryMutation() {
  const { apiClient, queryClient, sessionLifecycle } = useAdminApplication();
  const session = useAdminSession();
  return useMutation({
    mutationFn: async (input: InquiryMutation) => {
      const request = sessionLifecycle.createRequestController();
      try {
        if (input.kind === "assign") {
          return await assignInquiry(apiClient, input.inquiryId, request.controller.signal);
        }
        return await replyInquiry(apiClient, input.inquiryId, input.body, request.controller.signal);
      } finally {
        request.release();
      }
    },
    onSuccess: async (_result, input) => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: inquiryKeys.lists(session.sessionEpoch), refetchType: "none" }),
        queryClient.invalidateQueries({ queryKey: inquiryKeys.detail(session.sessionEpoch, input.inquiryId), refetchType: "none" }),
        queryClient.invalidateQueries({ queryKey: memberKeys.all(session.sessionEpoch), refetchType: "none" }),
      ]);
    },
  });
}

export function useReconcileInquiry() {
  const { apiClient, sessionLifecycle } = useAdminApplication();
  return useMutation({
    mutationFn: async ({
      inquiryId,
      query,
    }: {
      readonly inquiryId: number;
      readonly query: Pick<InquiryListQuery, "memberId" | "from" | "to">;
    }) => {
      const request = sessionLifecycle.createRequestController();
      try {
        return await findInquiryForReconciliation(
          apiClient,
          inquiryId,
          query,
          request.controller.signal,
        );
      } finally {
        request.release();
      }
    },
  });
}
